/**
 * AI Agent Manager Component
 *
 * Purpose: List + toolbar + MDTable for AI Agent Definitions with CRUD,
 *          override, revert, and restore (undelete) actions. Mirrors WorkflowDefinitionManager
 *          structure with simpler columns (no embedded children).
 * Author: Development Team
 * Created: 2026-05-23
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Typography,
} from '@mui/material';
import {
  Business as TenantIcon,
  Public as GlobalIcon,
  Restore as RestoreIcon,
  RestoreFromTrash as RestoreFromTrashIcon,
} from '@mui/icons-material';

import MDTable from '../md-table';
import { getScopeChipColor } from '../../constants/mdm';
import { TableColumn } from '../../types/table';
import aiAgentService, { AIAgent } from '../../services/aiAgentService';
import { TenantInfo } from '../../services/metadataService';
import { formatAuditTimestamp, formatAuditUser } from '../../utils/formatting';
import { ConfirmDialog, RowActionButton, RowActions, StatusChip } from '../common';
import AIAgentModal from './modals/AIAgentModal';
import { SecondaryButton } from '../common/ActionButtons';

interface AIAgentManagerProps {
  tenantContext: TenantInfo;
  canEdit: boolean;
}

const MAX_CHIPS_INLINE = 3;

function ChipStack({ values }: { values: string[] | undefined }) {
  if (!values || values.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary">
        &mdash;
      </Typography>
    );
  }
  const visible = values.slice(0, MAX_CHIPS_INLINE);
  const overflow = values.length - visible.length;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {visible.map((v) => (
        <Chip key={v} label={v} size="small" variant="outlined" />
      ))}
      {overflow > 0 && (
        <Chip label={`+${overflow} more`} size="small" sx={{ bgcolor: 'action.selected' }} />
      )}
    </Box>
  );
}

const AIAgentManager: React.FC<AIAgentManagerProps> = ({ tenantContext, canEdit }) => {
  const [data, setData] = useState<AIAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [selectedAgent, setSelectedAgent] = useState<AIAgent | undefined>();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingAgent, setDeletingAgent] = useState<AIAgent | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [revertDialogOpen, setRevertDialogOpen] = useState(false);
  const [revertingAgent, setRevertingAgent] = useState<AIAgent | null>(null);
  const [revertLoading, setRevertLoading] = useState(false);

  const [hideGlobal, setHideGlobal] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);

  // ORCH-363: restore is a write to a shared row (tenant-wide for GLOBAL
  // agents), so it is gated behind a confirmation like delete.
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [restoringAgent, setRestoringAgent] = useState<AIAgent | null>(null);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [restoreError, setRestoreError] = useState<string | null>(null);

  const [skillNameById, setSkillNameById] = useState<Record<string, string>>({});

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Options provide the skill id → name mapping for the grid; a failure
      // there must not block the agent list (chips fall back to raw ids).
      const [response, deleted, options] = await Promise.all([
        aiAgentService.listAIAgents({ tenant_id: tenantContext.tenant_id }),
        showDeleted
          ? aiAgentService.listAIAgents({ tenant_id: tenantContext.tenant_id, status: 'deleted' })
          : Promise.resolve([] as AIAgent[]),
        aiAgentService.getOptions().catch(() => null),
      ]);
      setData([...(response || []), ...(deleted || [])]);
      if (options?.skills) {
        setSkillNameById(Object.fromEntries(options.skills.map((s) => [s.id, s.name])));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load AI Agents');
    } finally {
      setLoading(false);
    }
  }, [tenantContext.tenant_id, showDeleted]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredData = useMemo(() => {
    if (hideGlobal) {
      return data.filter((item) => item.tenant_id !== 'GLOBAL');
    }
    return data;
  }, [data, hideGlobal]);

  const customToolbarFilters = useMemo(() => {
    const labelSx = {
      '& .MuiFormControlLabel-label': {
        fontSize: '0.875rem',
        color: 'text.secondary',
      },
    };
    return (
      <>
        <FormControlLabel
          control={
            <Checkbox
              checked={hideGlobal}
              onChange={(e) => setHideGlobal(e.target.checked)}
              data-testid="hide-global-checkbox"
            />
          }
          label="Hide GLOBAL"
          sx={labelSx}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={showDeleted}
              onChange={(e) => setShowDeleted(e.target.checked)}
              data-testid="show-deleted-checkbox"
            />
          }
          label="Show Deleted"
          sx={labelSx}
        />
      </>
    );
  }, [hideGlobal, showDeleted]);

  const handleCreate = () => {
    setSelectedAgent(undefined);
    setModalMode('create');
    setModalOpen(true);
  };

  const handleEdit = (agent: AIAgent) => {
    // Deleted rows are read-only until restored.
    if (agent.status === 'deleted') return;
    setSelectedAgent(agent);
    setModalMode('edit');
    setModalOpen(true);
  };

  // Shared GLOBAL agents can only be deleted from the GLOBAL tenant (ORCH-344);
  // in a client tenant the server refuses it, so don't offer it.
  const canDeleteRow = (agent: AIAgent) =>
    canEdit && (agent.tenant_id !== 'GLOBAL' || tenantContext.tenant_id === 'GLOBAL');

  const handleDeleteRequest = (agent: AIAgent) => {
    setDeletingAgent(agent);
    setDeleteError(null);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingAgent) return;
    try {
      await aiAgentService.deleteAIAgent(deletingAgent.ai_agent_id, deletingAgent.tenant_id);
      setDeleteDialogOpen(false);
      setDeletingAgent(null);
      await loadData();
    } catch (err) {
      console.error('Failed to delete AI Agent', err);
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete the AI Agent.');
    }
  };

  const handleRestoreRequest = (agent: AIAgent) => {
    setRestoringAgent(agent);
    setRestoreError(null);
    setRestoreDialogOpen(true);
  };

  const handleRestoreClose = () => {
    setRestoreDialogOpen(false);
    setRestoringAgent(null);
    setRestoreError(null);
  };

  const handleRestoreConfirm = async () => {
    if (!restoringAgent) return;
    setRestoreError(null);
    setRestoreLoading(true);
    try {
      await aiAgentService.restoreAIAgent(restoringAgent.ai_agent_id, restoringAgent.tenant_id);
      setRestoreDialogOpen(false);
      setRestoringAgent(null);
      await loadData();
    } catch (err) {
      console.error('Failed to restore AI Agent', err);
      setRestoreError(err instanceof Error ? err.message : 'Failed to restore the AI Agent.');
    } finally {
      setRestoreLoading(false);
    }
  };

  const handleRevertRequest = (agent: AIAgent) => {
    setRevertingAgent(agent);
    setRevertDialogOpen(true);
  };

  const handleRevertConfirm = async () => {
    if (!revertingAgent) return;
    setRevertLoading(true);
    try {
      await aiAgentService.revertAIAgent(revertingAgent.ai_agent_id, revertingAgent.tenant_id);
      setRevertDialogOpen(false);
      setRevertingAgent(null);
      await loadData();
    } catch (err) {
      console.error('Failed to revert AI Agent', err);
    } finally {
      setRevertLoading(false);
    }
  };

  const columns: TableColumn[] = [
    {
      field: 'label',
      headerName: 'Name',
      width: 220,
      sortable: true,
      renderCell: (value: string) => <Typography variant="body2">{value}</Typography>,
    },
    {
      field: 'skills',
      headerName: 'Skills',
      width: 220,
      sortable: false,
      renderCell: (value: string[]) => (
        <ChipStack values={(value || []).map((id) => skillNameById[id] ?? id)} />
      ),
    },
    {
      field: 'tool_calls',
      headerName: 'Tool Calls',
      width: 220,
      sortable: false,
      renderCell: (value: string[]) => <ChipStack values={value} />,
    },
    {
      field: 'system_prompt',
      headerName: 'System Prompt',
      width: 220,
      sortable: false,
      renderCell: (value: string) => (
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {value || '—'}
        </Typography>
      ),
    },
    {
      field: 'ai_agent_id',
      headerName: 'AI Agent ID',
      width: 200,
      sortable: true,
      renderCell: (value: string) => (
        <Box sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }} data-testid="ai-agent-id-cell">
          {value}
        </Box>
      ),
    },
    {
      field: 'tenant_id',
      headerName: 'Scope',
      width: 130,
      sortable: true,
      renderCell: (value: string, row: AIAgent) => {
        const scope = row.scope_info?.scope ?? (value === 'GLOBAL' ? 'GLOBAL' : 'LOCAL');
        const label = scope === 'Overridden' ? 'OVERRIDDEN' : scope;
        const icon =
          label === 'GLOBAL' ? <GlobalIcon fontSize="small" /> : <TenantIcon fontSize="small" />;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {icon}
            <Chip label={label} size="small" color={getScopeChipColor(label)} variant="outlined" />
          </Box>
        );
      },
    },
    {
      field: 'updated_at',
      headerName: 'Modified Date',
      width: 140,
      sortable: true,
      renderCell: (value: string) => formatAuditTimestamp(value),
    },
    {
      field: 'updated_by',
      headerName: 'Modified By',
      width: 140,
      sortable: true,
      renderCell: (value: string, row: AIAgent) => formatAuditUser(value, row.updated_by_user),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      sortable: true,
      renderCell: (value: string) => <StatusChip status={value} />,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 130,
      sortable: false,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      renderCell: (_v: any, row: AIAgent) =>
        row.status === 'deleted' ? (
          <RowActions
            entityLabel="AI Agent"
            rowLabel={row.label}
            testIdPrefix={`ai-agent-row-${row.ai_agent_id}`}
            rowTestId={`ai-agent-row-${row.ai_agent_id}`}
            secondaryActions={
              <RowActionButton
                label="Restore AI Agent"
                testId={`ai-agent-row-${row.ai_agent_id}-restore`}
                onClick={() => handleRestoreRequest(row)}
                disabled={!canDeleteRow(row)}
                disabledReason={
                  canEdit
                    ? 'Shared GLOBAL agents can only be restored in the GLOBAL tenant'
                    : undefined
                }
                icon={
                  <RestoreFromTrashIcon
                    fontSize="small"
                    color={canDeleteRow(row) ? 'primary' : 'disabled'}
                  />
                }
              />
            }
          />
        ) : (
          <RowActions
            entityLabel="AI Agent"
            rowLabel={row.label}
            testIdPrefix={`ai-agent-row-${row.ai_agent_id}`}
            rowTestId={`ai-agent-row-${row.ai_agent_id}`}
            onEdit={() => handleEdit(row)}
            secondaryActions={
              row.scope_info?.scope === 'Overridden' &&
              canEdit && (
                <RowActionButton
                  label="Revert to GLOBAL AI Agent"
                  testId={`ai-agent-row-${row.ai_agent_id}-revert`}
                  onClick={() => handleRevertRequest(row)}
                  icon={<RestoreIcon fontSize="small" color="warning" />}
                />
              )
            }
            onDelete={() => handleDeleteRequest(row)}
            deleteDisabled={!canDeleteRow(row)}
            deleteDisabledReason={
              canEdit ? 'Shared GLOBAL agents can only be deleted in the GLOBAL tenant' : undefined
            }
          />
        ),
    },
  ];

  return (
    <Box>
      <MDTable
        mode="manual"
        manual={{
          columns,
          title: 'AI Agents',
          description: `${filteredData.length} agent(s) found`,
        }}
        data={filteredData.map((item) => ({
          ...item,
          id: `${item.ai_agent_id}-${item.tenant_id}`,
        }))}
        loading={loading}
        error={error}
        onRowClick={handleEdit}
        onRefresh={loadData}
        onAddNew={canEdit ? handleCreate : undefined}
        showRefreshButton={true}
        showAddButton={canEdit}
        selectable={false}
        defaultSortModel={[{ field: 'label', sort: 'asc' }]}
        sortable={true}
        filterable={true}
        customToolbarFilters={customToolbarFilters}
        defaultColumnVisibility={{
          // Skills render as names (mapped via options) — visible by default.
          tool_calls: false,
          system_prompt: false,
          ai_agent_id: false,
        }}
      />

      <AIAgentModal
        open={modalOpen}
        mode={modalMode}
        aiAgent={selectedAgent}
        tenantContext={tenantContext}
        canEdit={canEdit}
        onClose={() => setModalOpen(false)}
        onSaved={loadData}
        onDelete={
          selectedAgent && canDeleteRow(selectedAgent)
            ? (agent) => {
                setModalOpen(false);
                handleDeleteRequest(agent);
              }
            : undefined
        }
      />

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete AI Agent</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the AI Agent &quot;{deletingAgent?.label}&quot;? This is
            a soft delete and the record can be recovered.
          </DialogContentText>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }} data-testid="ai-agent-delete-error">
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <SecondaryButton onClick={() => setDeleteDialogOpen(false)}>Cancel</SecondaryButton>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={restoreDialogOpen}
        title="Restore AI Agent?"
        confirmLabel="Restore"
        loading={restoreLoading}
        onConfirm={handleRestoreConfirm}
        onClose={handleRestoreClose}
        dataTestId="ai-agent-restore-dialog"
      >
        <DialogContentText>
          &quot;{restoringAgent?.label}&quot; becomes active again with its original ID, system
          prompt, skills and settings.
          {restoringAgent?.tenant_id === 'GLOBAL' &&
            ' It is a GLOBAL agent, so it becomes visible to every tenant again.'}
        </DialogContentText>
        {restoreError && (
          <Alert severity="error" sx={{ mt: 2 }} data-testid="ai-agent-restore-error">
            {restoreError}
          </Alert>
        )}
      </ConfirmDialog>

      <Dialog open={revertDialogOpen} onClose={() => setRevertDialogOpen(false)}>
        <DialogTitle>Revert to Global AI Agent</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to revert &quot;{revertingAgent?.label}&quot; to the GLOBAL
            version? Your tenant-specific customizations will be removed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <SecondaryButton onClick={() => setRevertDialogOpen(false)} disabled={revertLoading}>
            Cancel
          </SecondaryButton>
          <Button
            onClick={handleRevertConfirm}
            color="warning"
            variant="contained"
            disabled={revertLoading}
          >
            {revertLoading ? 'Reverting...' : 'Revert'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AIAgentManager;
